import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

/**
 * MY — Share Certificate (M32205) chatflow.
 *
 * 문서-뷰 Preset Studio:
 *   input-title → select-entity(skip 가능) → confirm
 *   + Person Lookup (Registered Holder ✨)
 */
export const shareCertificateFlow: ChatFlow = {
  id: 'share-certificate',
  taskCodeMatch: ['M32205'],
  categoryMatch: [
    'share certificate',
    'certificate of shares',
  ],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **Share Certificate**.\n\n` +
          `On the right, fill the live share certificate` +
          (docCount ? ` (${docCount} template linked)` : '') +
          `. Click **Registered Holder ✨** to look up the member, ` +
          `enter certificate / share details, set the **issue date**, and add **Director / Secretary signers**.\n\n` +
          `When ready, click **Generate** to create the task and document.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **entity** for this share certificate. You can skip if not linked yet.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        allowSkip: true,
        analysis: {
          success: true, placeholder: '_entity', sourceResolver: 'ENTITY', officerRole: null,
          varKey: '_entity', varLabel: 'Entity', confidence: 1,
          message: 'Select Entity (Share Certificate).',
          relatedFields: [], options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Fill the certificate on the right. Use **Registered Holder ✨**, share details, **📅**, and **Add signers**, then **Generate**.',
      widget: null,
      nextStep: null,
    },
    { id: 'officer-role', aiMessage: 'Please select a role for the person you want to look up.', widget: 'officer-role-buttons', awaitEvent: true },
    { id: 'officer-list', aiMessage: 'Select a person from the list.', widget: 'officer-list', awaitEvent: true },
    { id: 'officer-detail', aiMessage: 'Person details loaded. The document has been pre-filled.', widget: 'officer-field-picker', awaitEvent: true },
    { id: 'address-input', aiMessage: 'Enter the address below.', widget: 'address-input', awaitEvent: true },
    { id: 'address-list', aiMessage: 'This person has multiple addresses. Pick the one to use.', widget: 'address-options', awaitEvent: true },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [{ role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          entityCd, entityName, icpCd: entityCd,
          registrationNo: ent.registrationNo || '', entitySkipped: false, entityReselect: false,
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'entity-skip' && context.currentStepId === 'select-entity') {
      return {
        messages: [{ role: 'user', content: 'Skipped entity — continue without a linked company.', timestamp: Date.now(), stepId: 1 }],
        formUpdates: { entityCd: '', entityName: '', icpCd: '', registrationNo: '', tlinkData: null, entitySkipped: true },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!ctx.defaultRole) {
        return {
          messages: [{ role: 'user', content: `Look up a person for **${ctx.fieldGroup || 'this field'}**.`, timestamp: Date.now(), stepId: 1 }],
          nextStepId: 'officer-role',
          nextStepWidgetData: { entityCd },
        };
      }
      return {
        messages: [{ role: 'user', content: `Looking up **${ctx.defaultRole}**...`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: ctx.defaultRole },
      };
    }

    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      return {
        messages: [{ role: 'user', content: 'Person selected.', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-detail',
        nextStepWidgetData: payload,
      };
    }

    return {};
  },
};
